/**
 * Config de cliente resuelta en runtime (Worker + Hyperdrive) para CF-native.
 * Complementa `__EDGE_RUNTIME_CONFIG__` cuando las VITE_* no están en el build de Pages.
 */

const BRAND_RUNTIME_TTL_MS = 5 * 60 * 1000;
const SESSION_KEY = "brand_runtime_config_v1";

type BrandRuntimePayload = Record<string, string>;

let memoryCache: { at: number; data: BrandRuntimePayload } | null = null;
let inflight: Promise<BrandRuntimePayload | null> | null = null;

function applyToWindow(data: BrandRuntimePayload): void {
  if (typeof window === "undefined") return;
  window.__BRAND_RUNTIME_CONFIG__ = { ...window.__BRAND_RUNTIME_CONFIG__, ...data };
}

function readSession(): BrandRuntimePayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: number; data?: BrandRuntimePayload };
    if (!parsed?.data || typeof parsed.data !== "object") return null;
    if (typeof parsed.at === "number" && Date.now() - parsed.at > BRAND_RUNTIME_TTL_MS) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeSession(data: BrandRuntimePayload): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ at: Date.now(), data }),
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * Prefetch opcional — llamar temprano (main.tsx / landing) para que MP/Mux estén listos antes del CTA.
 */
export async function prefetchBrandRuntimeConfig(): Promise<BrandRuntimePayload | null> {
  if (typeof window === "undefined") return null;

  if (memoryCache && Date.now() - memoryCache.at < BRAND_RUNTIME_TTL_MS) {
    applyToWindow(memoryCache.data);
    return memoryCache.data;
  }

  const fromSession = readSession();
  if (fromSession) {
    memoryCache = { at: Date.now(), data: fromSession };
    applyToWindow(fromSession);
    return fromSession;
  }

  if (inflight) return inflight;

  inflight = (async () => {
    try {
      let res: Response | null = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          res = await fetch("/api/public/brand-runtime-config", {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
            cache: "default",
          });
          break;
        } catch {
          if (attempt === 3) return null;
          await new Promise((r) => setTimeout(r, 350 * attempt));
        }
      }
      if (!res?.ok) return null;
      const json = (await res.json()) as { vars?: BrandRuntimePayload };
      const vars = json?.vars;
      if (!vars || typeof vars !== "object") return null;
      memoryCache = { at: Date.now(), data: vars };
      writeSession(vars);
      applyToWindow(vars);
      return vars;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}